import React from 'react';
import { isNil } from 'lodash-es';

import { hasText } from '@/utils/strings';

export function useIframe(logoUrl: string | null = '') {
  const logoUrlRef = React.useRef(logoUrl);

  React.useEffect(() => {
    logoUrlRef.current = logoUrl;
  }, [logoUrl]);

  const postLogoToIframes = React.useCallback((logo?: string | null, targetIframe?: HTMLIFrameElement) => {
    const selectors = ['iframe#secondary-desktop', 'iframe#secondary-mobile'];

    const iframes = targetIframe
      ? [targetIframe]
      : selectors
          .map((selector) => document.querySelector<HTMLIFrameElement>(selector))
          .filter((iframe): iframe is HTMLIFrameElement => !isNil(iframe));

    iframes.forEach((iframeElement) => {
      if (isNil(iframeElement.contentWindow) || !hasText(logo)) {
        return;
      }

      iframeElement.contentWindow.postMessage(
        {
          type: 'UPDATE_LOGOS',
          payload: {
            footer: { src: logo },
            onboarding: { src: logo },
            topNavDesktop: { src: logo },
            topNavMobile: { src: logo },
          },
        },
        iframeElement.src,
      );
    });
  }, []);

  React.useEffect(() => {
    const handleIframeLoad = (event: Event) => {
      const iframeElement = event.target as HTMLIFrameElement;

      postLogoToIframes(logoUrlRef.current, iframeElement);
    };

    const attachListenerToIframe = (iframeElement: HTMLIFrameElement) => {
      if (iframeElement.dataset.logoListenerAttached) {
        return;
      }

      iframeElement.dataset.logoListenerAttached = 'true';

      if (iframeElement.contentDocument?.readyState === 'complete') {
        postLogoToIframes(logoUrlRef.current, iframeElement);
      }

      iframeElement.addEventListener('load', handleIframeLoad);
    };

    const selectors = ['iframe#secondary-desktop', 'iframe#secondary-mobile'];

    // 1. Check if they exist right now
    selectors.forEach((selector) => {
      const iframeElement = document.querySelector<HTMLIFrameElement>(selector);

      if (!isNil(iframeElement)) {
        attachListenerToIframe(iframeElement);
      }
    });

    // 2. Set up a MutationObserver to listen for when they are added to the DOM externally
    const observer = new MutationObserver(() => {
      selectors.forEach((selector) => {
        const iframeElement = document.querySelector<HTMLIFrameElement>(selector);

        if (!isNil(iframeElement)) {
          attachListenerToIframe(iframeElement);
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();

      selectors.forEach((selector) => {
        const iframeElement = document.querySelector<HTMLIFrameElement>(selector);

        if (!isNil(iframeElement)) {
          iframeElement.removeEventListener('load', handleIframeLoad);

          delete iframeElement.dataset.logoListenerAttached;
        }
      });
    };
  }, [postLogoToIframes]);

  React.useEffect(() => {
    postLogoToIframes(logoUrl);
  }, [logoUrl, postLogoToIframes]);
}
